import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  getProductTypes, createProductType, updateProductType, deleteProductType, mergeProductType,
  getCompanySettings, updateCompanySettings, getProductSettings, setProductSetting,
  getPackagingOptions, createPackagingOption, updatePackagingOption, deletePackagingOption,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProductTypes } from "@/context/ProductTypesContext";
import { Plus, Trash2, Pencil, Package, X, Check, ArrowDownToLine, ArrowUpFromLine, Combine, Tag, Droplets } from "lucide-react";
import { useT } from "@/i18n/I18nContext";
import { PackagingCard, PackedOnOrderCard } from "./company-settings/sections";

const unitLabel = (t, u) => (u === "l" ? t("products.unitL") : t("products.unitKg"));

// Manage the subproducts a product YIELDS (each has a unit).
function YieldManager({ product, onSave }) {
  const { t } = useT();
  const [draft, setDraft] = useState("");
  const [unit, setUnit] = useState("kg");
  const subs = product.subproducts || [];

  const add = async () => {
    const name = draft.trim();
    if (!name) return;
    if (subs.some((s) => s.name.toLowerCase() === name.toLowerCase())) {
      toast.error(t("products.duplicateSub"));
      return;
    }
    setDraft(""); setUnit("kg");
    await onSave([...subs, { name, unit }]);
  };
  const remove = async (name) => { await onSave(subs.filter((s) => s.name !== name)); };
  const toggleDeduct = async (name) =>
    onSave(subs.map((s) => (s.name === name ? { ...s, deduct_from_milk: !s.deduct_from_milk } : s)));

  return (
    <div className="rounded-lg border border-border bg-secondary/30 p-3" data-testid={`product-yields-${product.id}`}>
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
        <ArrowUpFromLine className="h-3.5 w-3.5" /> {t("products.yields")}
      </div>
      {subs.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t("products.noSubproducts")}</p>
      ) : (
        <div className="flex flex-wrap gap-1.5" data-testid={`product-yield-chips-${product.id}`}>
          {subs.map((s) => (
            <Badge key={s.name} variant="secondary" className="gap-1 pr-1 text-xs font-normal" data-testid={`product-yield-${product.id}-${s.name}`}>
              {s.name} <span className="text-muted-foreground">· {unitLabel(t, s.unit)}</span>
              <button type="button" onClick={() => toggleDeduct(s.name)}
                title={s.deduct_from_milk ? t("products.deductOn") : t("products.deductOff")}
                className={`rounded-sm p-0.5 transition-colors ${s.deduct_from_milk ? "text-[hsl(var(--brand))] hover:bg-[hsl(var(--brand)/0.15)]" : "text-muted-foreground/50 hover:bg-secondary hover:text-muted-foreground"}`}
                aria-label={`${t("products.deductToggle")} ${s.name}`} data-testid={`product-yield-deduct-${product.id}-${s.name}`}>
                <Droplets className="h-3 w-3" />
              </button>
              <button type="button" onClick={() => remove(s.name)}
                className="rounded-sm p-0.5 text-muted-foreground transition-colors hover:bg-[hsl(var(--danger)/0.15)] hover:text-[hsl(var(--danger))]"
                aria-label={`${t("common.delete")} ${s.name}`} data-testid={`product-yield-remove-${product.id}-${s.name}`}>
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <div className="mt-2.5 flex flex-wrap items-center gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={t("products.subproductPlaceholder")} className="h-8 max-w-[220px] text-sm"
          data-testid={`product-yield-input-${product.id}`} />
        <Select value={unit} onValueChange={setUnit}>
          <SelectTrigger className="h-8 w-20 text-sm" data-testid={`product-yield-unit-${product.id}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="kg">{t("products.unitKg")}</SelectItem>
            <SelectItem value="l">{t("products.unitL")}</SelectItem>
          </SelectContent>
        </Select>
        <Button size="sm" variant="outline" className="h-8" onClick={add} disabled={!draft.trim()} data-testid={`product-yield-add-${product.id}`}>
          <Plus className="mr-1 h-3.5 w-3.5" /> {t("products.addSubproduct")}
        </Button>
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">{t("products.yieldsHint")}</p>
    </div>
  );
}

// Manage the subproducts a product CONSUMES as raw material (names only).
function InputManager({ product, options, onSave }) {
  const { t } = useT();
  const inputs = product.input_subproducts || [];
  const available = options.filter((n) => !inputs.some((i) => i.toLowerCase() === n.toLowerCase()));

  const add = async (name) => { if (name && !inputs.includes(name)) await onSave([...inputs, name]); };
  const remove = async (name) => { await onSave(inputs.filter((i) => i !== name)); };

  return (
    <div className="rounded-lg border border-border bg-secondary/30 p-3" data-testid={`product-inputs-${product.id}`}>
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
        <ArrowDownToLine className="h-3.5 w-3.5" /> {t("products.consumes")}
      </div>
      {inputs.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t("products.consumesEmpty")}</p>
      ) : (
        <div className="flex flex-wrap gap-1.5" data-testid={`product-input-chips-${product.id}`}>
          {inputs.map((n) => (
            <Badge key={n} variant="secondary" className="gap-1 pr-1 text-xs font-normal" data-testid={`product-input-${product.id}-${n}`}>
              {n}
              <button type="button" onClick={() => remove(n)}
                className="rounded-sm p-0.5 text-muted-foreground transition-colors hover:bg-[hsl(var(--danger)/0.15)] hover:text-[hsl(var(--danger))]"
                aria-label={`${t("common.delete")} ${n}`} data-testid={`product-input-remove-${product.id}-${n}`}>
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
      <div className="mt-2.5">
        {options.length === 0 ? (
          <p className="text-[11px] text-muted-foreground">{t("products.noYieldsDefined")}</p>
        ) : available.length > 0 ? (
          <Select value="" onValueChange={add}>
            <SelectTrigger className="h-8 w-[260px] text-sm" data-testid={`product-input-select-${product.id}`}>
              <SelectValue placeholder={t("products.pickToConsume")} />
            </SelectTrigger>
            <SelectContent>
              {available.map((n) => (
                <SelectItem key={n} value={n} data-testid={`product-input-option-${product.id}-${n}`}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
      </div>
      <p className="mt-2 text-[11px] text-muted-foreground">{t("products.consumesHint")}</p>
    </div>
  );
}

// Manage sellable SUB-VARIANTS of a finished product (e.g. "Fresh Telemea" sold as-is with a
// shorter shelf life). Each variant is itself a product (linked via parent_product) so it flows
// into stock, sales, orders & price lists automatically; its shelf life lives in product-settings.
function SellableVariantManager({ parent, variants, shelfMap, canManage, onAdd, onRemove, onEdit }) {
  const { t } = useT();
  const [name, setName] = useState("");
  const [shelf, setShelf] = useState("");
  const [busy, setBusy] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editShelf, setEditShelf] = useState("");
  const [editBusy, setEditBusy] = useState(false);

  const add = async () => {
    const nm = name.trim();
    if (!nm) return;
    setBusy(true);
    try {
      await onAdd(nm, shelf === "" ? null : Number(shelf));
      setName(""); setShelf("");
    } finally { setBusy(false); }
  };

  const startEdit = (v) => {
    setEditId(v.id);
    setEditName(v.name);
    const sl = shelfMap[v.name];
    setEditShelf(sl == null ? "" : String(sl));
  };
  const cancelEdit = () => { setEditId(null); setEditName(""); setEditShelf(""); };
  const saveEdit = async (v) => {
    const nm = editName.trim();
    if (!nm) return;
    setEditBusy(true);
    try {
      await onEdit(v, nm, editShelf === "" ? null : Number(editShelf));
      cancelEdit();
    } finally { setEditBusy(false); }
  };

  return (
    <div className="mt-3 rounded-lg border border-border bg-secondary/30 p-3" data-testid={`product-variants-${parent.id}`}>
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
        <Tag className="h-3.5 w-3.5" /> {t("products.variantsTitle")}
      </div>
      {variants.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t("products.variantsEmpty")}</p>
      ) : (
        <div className="flex flex-col gap-1.5" data-testid={`product-variant-chips-${parent.id}`}>
          {variants.map((v) => {
            const sl = shelfMap[v.name];
            if (editId === v.id) {
              return (
                <div key={v.id} className="flex flex-wrap items-center gap-2" data-testid={`product-variant-edit-row-${v.id}`}>
                  <Input value={editName} onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEdit(v); } if (e.key === "Escape") cancelEdit(); }}
                    className="h-8 max-w-[200px] text-sm" data-testid={`product-variant-edit-name-${v.id}`} />
                  <div className="relative">
                    <Input type="number" min="0" value={editShelf} onChange={(e) => setEditShelf(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEdit(v); } if (e.key === "Escape") cancelEdit(); }}
                      placeholder={t("products.variantShelfPlaceholder")} className="h-8 w-28 pr-10 text-sm" data-testid={`product-variant-edit-shelf-${v.id}`} />
                    <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground">{t("products.days")}</span>
                  </div>
                  <Button size="icon" className="h-8 w-8" onClick={() => saveEdit(v)} disabled={!editName.trim() || editBusy}
                    aria-label={t("common.save")} data-testid={`product-variant-edit-save-${v.id}`}>
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={cancelEdit}
                    aria-label={t("common.cancel")} data-testid={`product-variant-edit-cancel-${v.id}`}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              );
            }
            return (
              <div key={v.id} className="flex items-center gap-1.5">
                <Badge variant="secondary" className="gap-1 text-xs font-normal" data-testid={`product-variant-${v.id}`}>
                  {v.name}
                  <span className="text-muted-foreground">· {sl != null ? t("products.variantShelfDays", { n: sl }) : t("products.variantNoShelf")}</span>
                </Badge>
                {canManage && (
                  <>
                    <button type="button" onClick={() => startEdit(v)}
                      className="rounded-sm p-1 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                      aria-label={`${t("common.edit")} ${v.name}`} data-testid={`product-variant-edit-${v.id}`}>
                      <Pencil className="h-3 w-3" />
                    </button>
                    <button type="button" onClick={() => onRemove(v)}
                      className="rounded-sm p-1 text-muted-foreground transition-colors hover:bg-[hsl(var(--danger)/0.15)] hover:text-[hsl(var(--danger))]"
                      aria-label={`${t("common.delete")} ${v.name}`} data-testid={`product-variant-remove-${v.id}`}>
                      <X className="h-3 w-3" />
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
      {canManage && (
        <div className="mt-2.5 flex flex-wrap items-center gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            placeholder={t("products.variantNamePlaceholder")} className="h-8 max-w-[220px] text-sm"
            data-testid={`product-variant-name-${parent.id}`} />
          <div className="relative">
            <Input type="number" min="0" value={shelf} onChange={(e) => setShelf(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
              placeholder={t("products.variantShelfPlaceholder")} className="h-8 w-32 pr-10 text-sm"
              data-testid={`product-variant-shelf-${parent.id}`} />
            <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] text-muted-foreground">{t("products.days")}</span>
          </div>
          <Button size="sm" variant="outline" className="h-8" onClick={add} disabled={!name.trim() || busy} data-testid={`product-variant-add-${parent.id}`}>
            <Plus className="mr-1 h-3.5 w-3.5" /> {t("products.addVariant")}
          </Button>
        </div>
      )}
      <p className="mt-2 text-[11px] text-muted-foreground">{t("products.variantsHint")}</p>
    </div>
  );
}

export default function Products() {
  const { t, td } = useT();
  const { can } = useAuth();
  const canManage = can("admin", "manager");
  const { reload: reloadContext, productTypes } = useProductTypes();

  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [shelfMap, setShelfMap] = useState({}); // product/variant name -> shelf_life_days
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newTemp, setNewTemp] = useState("");
  const [renameTarget, setRenameTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [mergeSource, setMergeSource] = useState(null);
  const [mergeInto, setMergeInto] = useState("");
  const [busy, setBusy] = useState(false);

  // Packaging + sales settings (relocated here from Company Settings).
  const [company, setCompany] = useState(null);
  const [packagings, setPackagings] = useState([]);
  const [packV, setPackV] = useState(0);
  const [newPack, setNewPack] = useState("");
  const [newNet, setNewNet] = useState("");

  const load = useCallback(async () => {
    try { setItems(await getProductTypes()); }
    catch (e) { console.error("products: load failed", e); }
    finally { setLoaded(true); }
  }, []);

  const loadSettings = useCallback(async () => {
    try {
      const s = await getProductSettings();
      const map = {};
      (s || []).forEach((row) => { if (row?.product_type) map[row.product_type] = row.shelf_life_days ?? null; });
      setShelfMap(map);
    } catch (e) { console.error("products: settings load failed", e); }
  }, []);

  const loadPack = useCallback(async () => {
    try { setPackagings(await getPackagingOptions()); setPackV((v) => v + 1); }
    catch (e) { console.error("products: packaging load failed", e); }
  }, []);

  const loadCompany = useCallback(async () => {
    try { setCompany(await getCompanySettings()); }
    catch (e) { console.error("products: company settings load failed", e); }
  }, []);

  useEffect(() => { load(); loadSettings(); if (canManage) { loadPack(); loadCompany(); } }, [load, loadSettings, loadPack, loadCompany, canManage]);

  // Split products into top-level vs. their sellable sub-variants.
  const topLevelItems = useMemo(() => items.filter((p) => !p.parent_product), [items]);
  const variantsByParent = useMemo(() => {
    const m = {};
    items.forEach((p) => { if (p.parent_product) (m[p.parent_product] = m[p.parent_product] || []).push(p); });
    return m;
  }, [items]);

  // All subproduct names produced anywhere — these are the options a product can consume.
  const allYieldNames = useMemo(() => {
    const set = new Map();
    items.forEach((p) => (p.subproducts || []).forEach((s) => { if (s?.name) set.set(s.name.toLowerCase(), s.name); }));
    return Array.from(set.values()).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const refresh = async () => { await load(); await loadSettings(); reloadContext(); };

  const addVariant = async (parentName, name, shelfDays) => {
    try {
      const created = await createProductType({ name, parent_product: parentName });
      if (shelfDays != null && !Number.isNaN(shelfDays)) {
        await setProductSetting(name, { shelf_life_days: shelfDays });
      }
      toast.success(t("products.variantAdded", { name }));
      await refresh();
      return created;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("products.variantError"));
      throw e;
    }
  };

  const removeVariant = async (v) => {
    try {
      await deleteProductType(v.id);
      toast.success(t("products.variantRemoved", { name: v.name }));
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("products.variantError"));
    }
  };

  const editVariant = async (v, newName, shelfDays) => {
    const nm = (newName || "").trim();
    if (!nm) return;
    try {
      if (nm !== v.name) {
        await updateProductType(v.id, { name: nm });
      }
      // Shelf life lives in product-settings keyed by product name; always write it under the
      // (possibly new) name so a rename keeps the correct shelf life.
      await setProductSetting(nm, { shelf_life_days: (shelfDays == null || Number.isNaN(shelfDays)) ? null : shelfDays });
      toast.success(t("products.variantUpdated", { name: nm }));
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("products.variantError"));
      throw e;
    }
  };

  const addProduct = async () => {
    const name = newName.trim();
    if (!name) { toast.error(t("products.nameRequired")); return; }
    setBusy(true);
    try {
      await createProductType({ name, label_name: newLabel.trim(), storage_temp: newTemp.trim(), subproducts: [], input_subproducts: [] });
      toast.success(t("products.created"));
      setNewName(""); setNewLabel(""); setNewTemp(""); setAddOpen(false);
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || t("products.saveFailed")); }
    finally { setBusy(false); }
  };

  const patchProduct = async (product, patch, optimistic) => {
    setItems((prev) => prev.map((p) => (p.id === product.id ? { ...p, ...optimistic } : p)));
    try {
      await updateProductType(product.id, patch);
      reloadContext();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("products.saveFailed"));
      await load();
    }
  };

  const saveRename = async () => {
    const name = (renameTarget?.name || "").trim();
    if (!name) { toast.error(t("products.nameRequired")); return; }
    setBusy(true);
    try {
      await updateProductType(renameTarget.id, { name, label_name: (renameTarget.label_name || "").trim(), storage_temp: (renameTarget.storage_temp || "").trim(), lot_prefix: (renameTarget.lot_prefix || "").trim() });
      toast.success(t("products.updated"));
      setRenameTarget(null);
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || t("products.saveFailed")); }
    finally { setBusy(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusy(true);
    try {
      await deleteProductType(deleteTarget.id);
      toast.success(t("products.deleted"));
      setDeleteTarget(null);
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || t("products.deleteFailed")); }
    finally { setBusy(false); }
  };

  const confirmMerge = async () => {
    if (!mergeSource || !mergeInto) return;
    setBusy(true);
    try {
      await mergeProductType(mergeSource.id, mergeInto);
      toast.success(t("products.merged", { from: mergeSource.name, to: mergeInto }));
      setMergeSource(null); setMergeInto("");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || t("products.mergeFailed")); }
    finally { setBusy(false); }
  };

  // ---- Packaging & sales settings handlers (auto-persist) ----
  const persistCompany = async (patch) => {
    try { await updateCompanySettings(patch); }
    catch (e) { toast.error(e?.response?.data?.detail || t("settings.failedSave")); loadCompany(); }
  };
  const setCompanyField = (k, v) => {
    setCompany((x) => ({ ...x, [k]: v }));
    persistCompany({ [k]: k === "min_maturation_days_for_sale" ? (Number(v) || 0) : v });
  };
  const togglePacked = (product) => {
    setCompany((x) => {
      const list = x.packed_on_order_products || [];
      const next = list.includes(product) ? list.filter((p) => p !== product) : [...list, product];
      persistCompany({ packed_on_order_products: next });
      return { ...x, packed_on_order_products: next };
    });
  };

  const addPack = async () => {
    const label = newPack.trim();
    if (!label) return;
    const net = newNet === "" ? null : Number(newNet);
    if (net !== null && (Number.isNaN(net) || net <= 0)) { toast.error(t("settings.netGt0")); return; }
    try {
      await createPackagingOption({ label, ...(net !== null ? { net_kg: net } : {}) });
      setNewPack(""); setNewNet(""); loadPack(); toast.success(t("settings.packAdded"));
    } catch (e) { toast.error(e?.response?.data?.detail || t("settings.failedAdd")); }
  };
  const removePack = async (id) => {
    try { await deletePackagingOption(id); loadPack(); toast.success(t("settings.packRemoved")); }
    catch (e) { toast.error(e?.response?.data?.detail || t("settings.failedRemove")); }
  };
  const toggleActive = async (p) => {
    try { await updatePackagingOption(p.id, { active: !(p.active !== false) }); loadPack(); }
    catch (e) { toast.error(t("settings.failedUpdate")); }
  };
  const saveNet = async (p, value) => {
    const net = value === "" ? null : Number(value);
    if (net === null) return;
    if (Number.isNaN(net) || net <= 0) { toast.error(t("settings.netGt0")); loadPack(); return; }
    if (Math.abs(net - (Number(p.net_kg) || 0)) < 1e-9) return; // unchanged
    try { await updatePackagingOption(p.id, { net_kg: net }); loadPack(); toast.success(t("settings.netUpdated", { label: p.label })); }
    catch (e) { toast.error(e?.response?.data?.detail || t("settings.failedUpdate")); }
  };
  const renamePack = async (p, value) => {
    const label = (value || "").trim();
    if (!label || label === p.label) return true; // unchanged / empty -> revert silently
    try {
      await updatePackagingOption(p.id, { label });
      await loadPack();
      toast.success(t("settings.packRenamed", { from: p.label, to: label }));
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("settings.failedUpdate"));
      return false;
    }
  };

  return (
    <Layout actions={
      canManage ? (
        <Button size="sm" onClick={() => setAddOpen(true)} data-testid="product-add-button">
          <Plus className="mr-1.5 h-4 w-4" /> {t("products.addProduct")}
        </Button>
      ) : null
    }>
      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[hsl(var(--brand)/0.10)] text-[hsl(var(--brand))]">
          <Package className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t("products.title")}</h1>
          <p className="max-w-2xl text-sm text-muted-foreground">{t("products.subtitle")}</p>
        </div>
      </div>

      {!loaded ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-32 animate-pulse rounded-[var(--radius)] border border-border bg-secondary/50" />)}
        </div>
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-sm text-muted-foreground" data-testid="products-empty">
            {t("products.empty")}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="products-list">
          {topLevelItems.map((p) => (
            <Card key={p.id} data-testid={`product-card-${p.id}`} className={p.active === false ? "opacity-70" : undefined}>
              <CardContent className="p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex min-w-0 flex-wrap items-center gap-2.5">
                    <div className="font-semibold" data-testid={`product-name-${p.id}`}>{p.name}</div>
                    {p.active === false ? (
                      <Badge className="border border-border bg-secondary text-muted-foreground">{t("products.inactive")}</Badge>
                    ) : (
                      <Badge className="badge-ok">{t("products.active")}</Badge>
                    )}
                    <Badge variant="outline" className="font-normal text-muted-foreground" data-testid={`product-subcount-${p.id}`}>
                      {t("products.subproductsCount", { n: (p.subproducts || []).length })}
                    </Badge>
                    {p.storage_temp ? (
                      <Badge variant="outline" className="font-normal text-muted-foreground" data-testid={`product-temp-${p.id}`}>
                        {t("products.storageTemp")}: {p.storage_temp}
                      </Badge>
                    ) : null}
                    {p.lot_prefix ? (
                      <Badge variant="outline" className="font-mono font-normal text-muted-foreground" data-testid={`product-lot-prefix-${p.id}`}>
                        {t("products.lotPrefix")}: {p.lot_prefix}
                      </Badge>
                    ) : null}
                  </div>
                  {canManage && (
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1.5">
                        <Switch checked={p.active !== false} onCheckedChange={() => patchProduct(p, { active: !(p.active !== false) }, { active: !(p.active !== false) })} data-testid={`product-active-${p.id}`} />
                        <Label className="text-xs text-muted-foreground">{p.active === false ? t("products.inactive") : t("products.active")}</Label>
                      </div>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setRenameTarget({ id: p.id, name: p.name, label_name: p.label_name || "", storage_temp: p.storage_temp || "", lot_prefix: p.lot_prefix || "" })} data-testid={`product-rename-${p.id}`} aria-label={t("products.rename")}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => { setMergeSource(p); setMergeInto(""); }} data-testid={`product-merge-${p.id}`} aria-label={t("products.merge")} title={t("products.merge")}>
                        <Combine className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" className="h-8 w-8 text-[hsl(var(--danger))] hover:text-[hsl(var(--danger))]" onClick={() => setDeleteTarget(p)} data-testid={`product-delete-${p.id}`} aria-label={t("common.delete")}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>

                {canManage ? (
                  <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
                    <YieldManager product={p} onSave={(subs) => patchProduct(p, { subproducts: subs }, { subproducts: subs })} />
                    <InputManager product={p} options={allYieldNames} onSave={(inp) => patchProduct(p, { input_subproducts: inp }, { input_subproducts: inp })} />
                  </div>
                ) : (
                  (p.subproducts || []).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {(p.subproducts || []).map((s) => (
                        <Badge key={s.name} variant="secondary" className="text-xs font-normal">{s.name} · {unitLabel(t, s.unit)}</Badge>
                      ))}
                    </div>
                  )
                )}

                <SellableVariantManager
                  parent={p}
                  variants={variantsByParent[p.name] || []}
                  shelfMap={shelfMap}
                  canManage={canManage}
                  onAdd={(name, shelf) => addVariant(p.name, name, shelf)}
                  onRemove={removeVariant}
                  onEdit={editVariant}
                />
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {canManage && company && (
        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-12" data-testid="products-packaging-settings">
          <PackagingCard
            packagings={packagings} packV={packV}
            newPack={newPack} setNewPack={setNewPack} newNet={newNet} setNewNet={setNewNet}
            addPack={addPack} removePack={removePack} toggleActive={toggleActive} saveNet={saveNet} renamePack={renamePack}
          />
          <PackedOnOrderCard f={company} set={setCompanyField} productTypes={productTypes} td={td} togglePacked={togglePacked} />
        </div>
      )}

      {/* Add product dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="product-add-dialog">
          <DialogHeader>
            <DialogTitle>{t("products.addProduct")}</DialogTitle>
            <DialogDescription>{t("products.subtitle")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="new-product-name">{t("products.productName")}</Label>
              <Input id="new-product-name" value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addProduct(); } }}
                placeholder={t("products.productPlaceholder")} data-testid="product-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-product-label">{t("products.labelName")}</Label>
              <Input id="new-product-label" value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
                placeholder={t("products.labelNamePlaceholder")} data-testid="product-label-input" />
              <p className="text-[11px] text-muted-foreground">{t("products.labelNameHint")}</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-product-temp">{t("products.storageTemp")}</Label>
              <Input id="new-product-temp" value={newTemp} onChange={(e) => setNewTemp(e.target.value)} placeholder={t("products.tempPlaceholder")} data-testid="product-temp-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} data-testid="product-add-cancel">{t("common.cancel")}</Button>
            <Button onClick={addProduct} disabled={busy} data-testid="product-add-save">{busy ? t("common.saving") : t("common.create")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename dialog */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent data-testid="product-rename-dialog">
          <DialogHeader><DialogTitle>{t("products.renameTitle")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="rename-product-name">{t("products.productName")}</Label>
              <Input id="rename-product-name" value={renameTarget?.name || ""}
                onChange={(e) => setRenameTarget((r) => ({ ...r, name: e.target.value }))}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveRename(); } }}
                data-testid="product-rename-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rename-product-label">{t("products.labelName")}</Label>
              <Input id="rename-product-label" value={renameTarget?.label_name || ""}
                onChange={(e) => setRenameTarget((r) => ({ ...r, label_name: e.target.value }))}
                placeholder={t("products.labelNamePlaceholder")} data-testid="product-rename-label" />
              <p className="text-[11px] text-muted-foreground">{t("products.labelNameHint")}</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rename-product-temp">{t("products.storageTemp")}</Label>
              <Input id="rename-product-temp" value={renameTarget?.storage_temp || ""}
                onChange={(e) => setRenameTarget((r) => ({ ...r, storage_temp: e.target.value }))}
                placeholder={t("products.tempPlaceholder")} data-testid="product-rename-temp" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rename-product-lot-prefix">{t("products.lotPrefix")}</Label>
              <Input id="rename-product-lot-prefix" value={renameTarget?.lot_prefix || ""}
                onChange={(e) => setRenameTarget((r) => ({ ...r, lot_prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 5) }))}
                placeholder="TL" maxLength={5} className="w-28 font-mono uppercase" data-testid="product-rename-lot-prefix" />
              <p className="text-[11px] text-muted-foreground">{t("products.lotPrefixHint")}</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)} data-testid="product-rename-cancel">{t("common.cancel")}</Button>
            <Button onClick={saveRename} disabled={busy} data-testid="product-rename-save">{busy ? t("common.saving") : t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="product-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("products.title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("products.confirmDelete", { name: deleteTarget?.name || "" })}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="product-delete-cancel">{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={busy}
              className="bg-[hsl(var(--danger))] text-white hover:bg-[hsl(var(--danger))]/90" data-testid="product-delete-confirm">
              {busy ? t("common.deleting") : t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!mergeSource} onOpenChange={(o) => { if (!o) { setMergeSource(null); setMergeInto(""); } }}>
        <DialogContent data-testid="product-merge-dialog">
          <DialogHeader>
            <DialogTitle>{t("products.mergeTitle", { name: mergeSource?.name || "" })}</DialogTitle>
            <DialogDescription>{t("products.mergeHint")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label>{t("products.mergeInto")}</Label>
            <Select value={mergeInto} onValueChange={setMergeInto}>
              <SelectTrigger data-testid="product-merge-select"><SelectValue placeholder={t("products.mergeSelect")} /></SelectTrigger>
              <SelectContent>
                {items.filter((x) => x.id !== mergeSource?.id).map((x) => (
                  <SelectItem key={x.id} value={x.name} data-testid={`product-merge-option-${x.id}`}>{x.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMergeSource(null); setMergeInto(""); }} data-testid="product-merge-cancel">{t("common.cancel")}</Button>
            <Button onClick={confirmMerge} disabled={busy || !mergeInto} data-testid="product-merge-confirm">
              {busy ? t("common.saving") : t("products.mergeConfirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
